import { useEffect, useRef } from 'react';

/**
 * Canvas “start” control from start_button_app_preview.html (visual + interaction).
 * Salon landing only — not used on skin flow.
 */
export default function SalonStartCanvasButton({
  onComplete,
  disabled = false,
  label = 'Start',
  primaryColor = '',
  secondaryColor = '',
  textColor = '',
  className = '',
}) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const onCompleteRef = useRef(onComplete);
  const disabledRef = useRef(disabled);
  const labelRef = useRef(label);
  const primaryColorRef = useRef(primaryColor);
  const secondaryColorRef = useRef(secondaryColor);
  const textColorRef = useRef(textColor);
  onCompleteRef.current = onComplete;
  disabledRef.current = disabled;
  labelRef.current = label;
  primaryColorRef.current = primaryColor;
  secondaryColorRef.current = secondaryColor;
  textColorRef.current = textColor;

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return undefined;
    const ctx = canvas.getContext('2d');

    const DEFAULT_CTA_PRIMARY = '#A8EDEA';
    const DEFAULT_CTA_SECONDARY = '#FFB3CC';
    const DEFAULT_CTA_TEXT = '#FFFFFF';
    let ctaPrimary = DEFAULT_CTA_PRIMARY;
    let ctaSecondary = DEFAULT_CTA_SECONDARY;
    let ctaTextColor = DEFAULT_CTA_TEXT;

    const normalizeCssVar = (value, fallback) => {
      if (!value || typeof value !== 'string') return fallback;
      const trimmed = value.trim();
      return trimmed || fallback;
    };

    const refreshCtaColors = () => {
      if (typeof window === 'undefined') return;
      const computed = getComputedStyle(document.documentElement);
      const rawPrimary = computed.getPropertyValue('--cta-primary');
      const rawSecondary = computed.getPropertyValue('--cta-secondary');
      const rawText = computed.getPropertyValue('--cta-text-color');
      const fromPropsPrimary = normalizeCssVar(primaryColorRef.current, '');
      const fromPropsSecondary = normalizeCssVar(secondaryColorRef.current, '');
      const fromPropsText = normalizeCssVar(textColorRef.current, '');

      ctaPrimary = fromPropsPrimary || normalizeCssVar(rawPrimary, DEFAULT_CTA_PRIMARY);
      ctaSecondary = fromPropsSecondary || normalizeCssVar(rawSecondary, ctaPrimary || DEFAULT_CTA_SECONDARY);
      ctaTextColor = fromPropsText || normalizeCssVar(rawText, DEFAULT_CTA_TEXT);
    };

    const getCtaRgba = (alpha) => rgbaOpacity(ctaPrimary, alpha);
    const getCtaSecondaryRgba = (alpha) => rgbaOpacity(ctaSecondary, alpha);

    const PHASE = { IDLE: 0, HIDING: 1, BURST: 2, SHOCKWAVE: 3, LIGHTNING: 4, DONE: 5 };
    let phase = PHASE.IDLE;
    let phaseT = 0;
    let hideScale = 1;
    let hideSpin = 0;
    let hideAlpha = 1;
    let a1 = 0;
    let a2 = Math.PI * 0.7;
    let a3 = Math.PI * 1.4;
    let particles = [];
    let shockwaves = [];
    let lightSeed = 0;
    let btnScale = 1;
    let navigated = false;
    /** True right after burst sequence completes — drives loading-loop draw until unmount */
    let postDoneLoading = false;
    let lastTime = null;
    let rafId = 0;
    let CX = 0;
    let CY = 0;
    let R = 0;

    const getParticleColors = () => [
      ctaPrimary,
      ctaSecondary,
      rgbaOpacity(ctaPrimary, 0.75),
      rgbaOpacity(ctaSecondary, 0.75),
    ];

    function rgbaOpacity(color, a) {
      if (color.startsWith('rgba')) {
        return color.replace(/[\d.]+\)$/, `${Math.min(1, parseFloat(a.toFixed(3)))})`);
      }
      if (color.startsWith('rgb(')) {
        return color.replace('rgb(', 'rgba(').replace(')', `,${a})`);
      }
      if (color.startsWith('#')) {
        const r = parseInt(color.slice(1, 3), 16);
        const g = parseInt(color.slice(3, 5), 16);
        const b = parseInt(color.slice(5, 7), 16);
        return `rgba(${r},${g},${b},${a})`;
      }
      return color;
    }

    function seededRng(seed) {
      let s = seed | 0;
      return () => {
        s |= 0;
        s = (s + 0x6d2b79f5) | 0;
        let t = Math.imul(s ^ (s >>> 15), 1 | s);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
      };
    }

    function spawnParticles(n) {
      const cols = getParticleColors();
      for (let i = 0; i < n; i += 1) {
        const ang = Math.random() * Math.PI * 2;
        const sp = 60 + Math.random() * 300;
        particles.push({
          x: CX,
          y: CY,
          vx: Math.cos(ang) * sp,
          vy: Math.sin(ang) * sp,
          life: 0.5 + Math.random() * 0.9,
          size: 1.5 + Math.random() * 5,
          color: cols[(Math.random() * cols.length) | 0],
        });
      }
    }

    function spawnShock(color, maxRatio) {
      shockwaves.push({ r: R * 0.18, maxR: R * maxRatio, life: 1, color });
    }

    function updateParticles(dt) {
      particles.forEach((p) => {
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.vy += 80 * dt;
        p.life -= dt * 1.8;
      });
      particles = particles.filter((p) => p.life > 0);
    }

    function updateShocks(dt) {
      shockwaves.forEach((s) => {
        s.r += (s.maxR - s.r) * dt * 5;
        s.life -= dt * 2.2;
      });
      shockwaves = shockwaves.filter((s) => s.life > 0);
    }

    function transition(next) {
      phase = next;
      phaseT = 0;
      if (next === PHASE.BURST) {
        spawnShock('rgba(255,255,255,0.9)', 1.25);
        spawnShock(getCtaRgba(0.8), 1.05);
        spawnParticles(70);
      }
      if (next === PHASE.SHOCKWAVE) {
        spawnShock('rgba(255,255,255,0.95)', 1.35);
        spawnShock(getCtaSecondaryRgba(0.7), 0.95);
        spawnParticles(45);
      }
      if (next === PHASE.LIGHTNING) {
        spawnShock('rgba(255,255,255,0.8)', 1.15);
      }
    }

    function drawShock(s) {
      ctx.beginPath();
      ctx.arc(CX, CY, s.r, 0, Math.PI * 2);
      ctx.strokeStyle = rgbaOpacity(s.color, s.life * 0.7);
      ctx.lineWidth = 4 * s.life;
      ctx.shadowColor = s.color;
      ctx.shadowBlur = 12 * s.life;
      ctx.stroke();
      ctx.shadowBlur = 0;
    }

    function arc(cx, cy, r, start, sweep, color, sw, glowW) {
      ctx.beginPath();
      ctx.arc(cx, cy, r, start, start + sweep);
      ctx.strokeStyle = color;
      ctx.lineWidth = glowW;
      ctx.lineCap = 'round';
      ctx.shadowColor = color;
      /* Cap blur so glow + stroke stays inside bitmap (avoids square edge artifact if clip fails) */
      ctx.shadowBlur = Math.min(glowW * 3.5, R * 0.26);
      ctx.stroke();
      ctx.shadowBlur = 0;
      ctx.beginPath();
      ctx.arc(cx, cy, r, start, start + sweep);
      ctx.strokeStyle = color;
      ctx.lineWidth = sw;
      ctx.lineCap = 'round';
      ctx.stroke();
    }

    function drawAmbientGlow() {
      const op =
        phase === PHASE.IDLE
          ? 0.1 + Math.sin(phaseT * 2.5) * 0.04
          : phase === PHASE.HIDING
            ? 0.1 + (phaseT / 0.55) * 0.28
            : phase === PHASE.BURST
              ? Math.max(0, 0.5 - phaseT * 0.4)
              : phase === PHASE.SHOCKWAVE
                ? 0.35
                : phase === PHASE.LIGHTNING
                  ? 0.25
                  : 0.08;
      const g = ctx.createRadialGradient(CX, CY, 0, CX, CY, R * 0.9);
      g.addColorStop(0, `rgba(255,255,255,${op})`);
      g.addColorStop(0.5, `rgba(255,255,255,${op * 0.35})`);
      g.addColorStop(1, 'transparent');
      ctx.beginPath();
      ctx.arc(CX, CY, R * 0.9, 0, Math.PI * 2);
      ctx.fillStyle = g;
      ctx.fill();
    }

    function drawArcs() {
      const isHiding = phase === PHASE.HIDING;
      const pinkSweep = isHiding ? Math.PI * 1.55 * (1 - (phaseT / 0.55) * 0.5) : Math.PI * 1.55;
      const alpha = isHiding ? hideAlpha : 1;
      arc(CX, CY, R * 0.87, a1, pinkSweep, getCtaRgba(0.92 * alpha), R * 0.05, R * 0.085);
      arc(
        CX,
        CY,
        R * 0.72,
        a2,
        Math.PI * 0.5 * (isHiding ? 1 - (phaseT / 0.55) * 0.7 : 1),
        getCtaRgba(0.95 * alpha),
        R * 0.043,
        R * 0.068,
      );
      arc(
        CX,
        CY,
        R * 0.72,
        a2 + Math.PI * 0.9,
        Math.PI * 0.55 * (isHiding ? 1 - (phaseT / 0.55) * 0.7 : 1),
        getCtaSecondaryRgba(0.95 * alpha),
        R * 0.043,
        R * 0.068,
      );
      arc(CX, CY, R * 0.58, a3, Math.PI * 0.28, getCtaSecondaryRgba(0.55 * alpha), R * 0.026, R * 0.042);
    }

    function drawTicks() {
      const r = R * 0.62;
      for (let i = 0; i < 48; i += 1) {
        const angle = (i / 48) * Math.PI * 2 - Math.PI / 2;
        const major = i % 6 === 0;
        ctx.beginPath();
        ctx.moveTo(CX + Math.cos(angle) * r, CY + Math.sin(angle) * r);
        ctx.lineTo(CX + Math.cos(angle) * (r + (major ? 7 : 4)), CY + Math.sin(angle) * (r + (major ? 7 : 4)));
        ctx.strokeStyle = `rgba(255,255,255,${major ? 0.5 : 0.18})`;
        ctx.lineWidth = major ? 1.5 : 0.7;
        ctx.stroke();
      }
    }

    function drawOrb() {
      const r = R * 0.44;
      const g = ctx.createRadialGradient(CX, CY * 0.92, 0, CX, CY, r);
      g.addColorStop(0, 'rgba(255,255,255,0.28)');
      g.addColorStop(0.32, getCtaRgba(0.2));
      g.addColorStop(0.65, getCtaSecondaryRgba(0.18));
      g.addColorStop(1, getCtaRgba(0.12));
      ctx.beginPath();
      ctx.arc(CX, CY, r, 0, Math.PI * 2);
      ctx.fillStyle = g;
      ctx.fill();
      ctx.beginPath();
      ctx.arc(CX, CY, r, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(255,255,255,0.18)';
      ctx.lineWidth = r * 0.04;
      ctx.stroke();
      const hl = ctx.createRadialGradient(CX - r * 0.2, CY - r * 0.25, 0, CX, CY, r * 0.6);
      hl.addColorStop(0, getCtaRgba(0.22));
      hl.addColorStop(1, 'transparent');
      ctx.beginPath();
      ctx.arc(CX, CY, r, 0, Math.PI * 2);
      ctx.fillStyle = hl;
      ctx.fill();
      if (phase !== PHASE.IDLE) {
        const t = 1;
        ctx.beginPath();
        ctx.arc(CX, CY, r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${t * 0.12})`;
        ctx.shadowColor = 'rgba(255,255,255,0.6)';
        ctx.shadowBlur = 10;
        ctx.fill();
        ctx.shadowBlur = 0;
      }
    }

    function drawGlowRing() {
      const r = R * 0.44;
      ctx.beginPath();
      ctx.arc(CX, CY, r, 0, Math.PI * 2);
      ctx.strokeStyle = getCtaSecondaryRgba(0.3);
      ctx.lineWidth = r * 0.07;
      ctx.shadowColor = getCtaSecondaryRgba(0.55);
      ctx.shadowBlur = r * 0.14;
      ctx.stroke();
      ctx.shadowBlur = 0;
      ctx.beginPath();
      ctx.arc(CX, CY, r, 0, Math.PI * 2);
      ctx.strokeStyle = getCtaRgba(0.95);
      ctx.lineWidth = r * 0.018;
      ctx.stroke();
    }

    function ray(cx, cy, angle, len, w, color) {
      const ex = cx + Math.cos(angle) * len;
      const ey = cy + Math.sin(angle) * len;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(ex, ey);
      ctx.strokeStyle = color;
      ctx.lineWidth = w;
      ctx.lineCap = 'round';
      ctx.shadowColor = color;
      ctx.shadowBlur = w * 5;
      ctx.stroke();
      ctx.shadowBlur = 0;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(ex, ey);
      ctx.strokeStyle = 'rgba(255,255,255,0.4)';
      ctx.lineWidth = w * 0.28;
      ctx.stroke();
    }

    function drawRays() {
      const dur = phase === PHASE.BURST ? 0.5 : 0.7;
      const t = Math.min(phaseT / dur, 1);
      const rayLen = R * (0.38 + t * 0.55);
      const alpha = 1 - t * 0.6;
      const rng = seededRng(42);
      for (let i = 0; i < 10; i += 1) {
        const angle = (i / 10) * Math.PI * 2 + Math.PI * 0.05;
        const len = rayLen * (0.7 + rng() * 0.5);
        const w = R * (0.009 + rng() * 0.01);
        ray(CX, CY, angle, len, w, getCtaRgba(alpha * (0.65 + rng() * 0.35)));
      }
      for (let i = 0; i < 8; i += 1) {
        const angle = (i / 8) * Math.PI * 2 + Math.PI * 0.5;
        const len = rayLen * 0.6 * (0.5 + rng() * 0.5);
        ray(CX, CY, angle, len, R * 0.006, getCtaSecondaryRgba(alpha * 0.9));
      }
    }

    function bolt(cx, cy, angle, length, alpha, rng) {
      const segs = 6;
      const path = new Path2D();
      path.moveTo(cx, cy);
      let x = cx;
      let y = cy;
      for (let i = 0; i < segs; i += 1) {
        const seg = length / segs;
        const j = (rng() - 0.5) * seg * 0.8;
        const perp = angle + Math.PI / 2;
        x += Math.cos(angle) * seg + Math.cos(perp) * j;
        y += Math.sin(angle) * seg + Math.sin(perp) * j;
        path.lineTo(x, y);
      }
      ctx.strokeStyle = getCtaSecondaryRgba(alpha * 0.5);
      ctx.lineWidth = 5;
      ctx.shadowColor = getCtaSecondaryRgba(0.7);
      ctx.shadowBlur = 8;
      ctx.lineJoin = 'round';
      ctx.stroke(path);
      ctx.shadowBlur = 0;
      ctx.strokeStyle = `rgba(255,255,255,${alpha * 0.95})`;
      ctx.lineWidth = 1.5;
      ctx.stroke(path);
    }

    function drawLightning() {
      const alpha = 1 - phaseT / 0.9;
      const seed = Math.floor(lightSeed / 3);
      const rng = seededRng(seed);
      for (let i = 0; i < 14; i += 1) {
        const angle = (i / 14) * Math.PI * 2 + rng() * 0.5;
        const length = R * (0.38 + rng() * 0.5);
        const a = alpha * (0.4 + rng() * 0.6);
        bolt(CX, CY, angle, length, a, seededRng(seed * 100 + i));
      }
    }

    function drawParticle(p) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size * p.life, 0, Math.PI * 2);
      ctx.fillStyle = rgbaOpacity(p.color, Math.max(0, Math.min(1, p.life)));
      ctx.shadowColor = p.color;
      ctx.shadowBlur = p.size * 2.5;
      ctx.fill();
      ctx.shadowBlur = 0;
    }

    function drawLabel() {
      const la =
        phase === PHASE.HIDING
          ? Math.max(0, hideAlpha)
          : phase === PHASE.BURST || phase === PHASE.SHOCKWAVE
            ? Math.max(0, 1 - phaseT * 2)
            : phase === PHASE.LIGHTNING
              ? Math.min(0.35, phaseT / 0.9)
              : 1;
      const ctaLabel =
        typeof labelRef.current === 'string' && labelRef.current.trim()
          ? labelRef.current.trim()
          : 'Start';
                  const length = ctaLabel.length;
                  const fontScale = length > 20 ? 0.062 : length > 12 ? 0.078 : 0.13;
      ctx.textAlign = 'center';
                  ctx.font = `800 ${R * fontScale}px Nunito,sans-serif`;
                  ctx.fillStyle = rgbaOpacity(ctaTextColor, la);
      ctx.shadowColor = 'rgba(0,0,0,0.2)';
      ctx.shadowBlur = 6;
                  ctx.fillText(ctaLabel, CX, CY + R * 0.045, R * 1.5);
      ctx.shadowBlur = 0;
    }

    /** Looping visuals while session is preparing (delay + API) — matches orb language of idle control */
    function drawLoadingState() {
      const pulse = 0.12 + Math.sin(phaseT * 3) * 0.06;
      const g = ctx.createRadialGradient(CX, CY, 0, CX, CY, R * 0.9);
      g.addColorStop(0, `rgba(255,255,255,${0.14 + pulse})`);
      g.addColorStop(0.55, `rgba(255,255,255,${0.06 + pulse * 0.5})`);
      g.addColorStop(1, 'transparent');
      ctx.beginPath();
      ctx.arc(CX, CY, R * 0.9, 0, Math.PI * 2);
      ctx.fillStyle = g;
      ctx.fill();

      drawOrb();
      drawGlowRing();

      const spin = phaseT * 1.8;
      const rTrack = R * 0.78;
      ctx.beginPath();
      ctx.arc(CX, CY, rTrack, spin, spin + Math.PI * 1.35);
      ctx.strokeStyle = 'rgba(255,255,255,0.22)';
      ctx.lineWidth = R * 0.04;
      ctx.lineCap = 'round';
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(CX, CY, rTrack, spin + 0.15, spin + Math.PI * 1.35);
      ctx.strokeStyle = getCtaRgba(0.7);
      ctx.lineWidth = R * 0.028;
      ctx.shadowColor = getCtaRgba(0.5);
      ctx.shadowBlur = R * 0.06;
      ctx.stroke();
      ctx.shadowBlur = 0;

      ctx.textAlign = 'center';
      ctx.font = `700 ${R * 0.08}px Nunito,sans-serif`;
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      ctx.fillText('…', CX, CY + R * 0.06);
    }

    function draw() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (phase === PHASE.DONE && postDoneLoading) {
        drawLoadingState();
        return;
      }

      shockwaves.forEach((s) => drawShock(s));
      particles.forEach((p) => drawParticle(p));
      ctx.save();
      ctx.translate(CX, CY);
      ctx.scale(btnScale, btnScale);
      if (phase === PHASE.HIDING) {
        ctx.rotate(hideSpin);
        ctx.scale(hideScale, hideScale);
      }
      ctx.translate(-CX, -CY);
      if (
        phase !== PHASE.BURST &&
        phase !== PHASE.SHOCKWAVE &&
        phase !== PHASE.LIGHTNING &&
        phase !== PHASE.DONE
      ) {
        drawAmbientGlow();
        drawArcs();
        drawTicks();
        drawOrb();
        drawGlowRing();
        drawLabel();
      } else {
        drawAmbientGlow();
      }
      if (phase === PHASE.BURST || phase === PHASE.SHOCKWAVE) drawRays();
      if (phase === PHASE.LIGHTNING) drawLightning();
      ctx.restore();
    }

    function loop(ts) {
      rafId = requestAnimationFrame(loop);
      if (!lastTime) {
        lastTime = ts;
        return;
      }
      let dt = (ts - lastTime) / 1000;
      lastTime = ts;
      dt = Math.min(dt, 0.05);
      phaseT += dt;
      lightSeed += 1;

      const spd =
        phase === PHASE.IDLE
          ? 1.8
          : phase === PHASE.HIDING
            ? 8 + (phaseT / 0.55) * 14
            : phase === PHASE.BURST
              ? 18
              : phase === PHASE.SHOCKWAVE
                ? 14
                  : phase === PHASE.LIGHTNING
                  ? 10
                  : phase === PHASE.DONE && postDoneLoading
                    ? 2.2
                    : 1;
      a1 += dt * spd * 0.8;
      a2 -= dt * spd * 1.1;
      a3 += dt * spd * 1.4;

      if (phase === PHASE.HIDING) {
        const t = Math.min(phaseT / 0.55, 1);
        const ease = t * t * t;
        hideScale = 1 - ease * 0.92;
        hideSpin = ease * Math.PI * 1.5;
        hideAlpha = 1 - ease;
        if (t >= 1) transition(PHASE.BURST);
      }
      if (phase === PHASE.BURST && phaseT > 0.5) transition(PHASE.SHOCKWAVE);
      if (phase === PHASE.SHOCKWAVE && phaseT > 0.7) transition(PHASE.LIGHTNING);
      if (phase === PHASE.LIGHTNING && phaseT > 0.9) transition(PHASE.DONE);
      if (phase === PHASE.DONE && !navigated) {
        navigated = true;
        postDoneLoading = true;
        const fn = onCompleteRef.current;
        if (typeof fn === 'function') fn();
      }

      if (phase === PHASE.BURST || phase === PHASE.SHOCKWAVE) spawnParticles(5);
      updateParticles(dt);
      updateShocks(dt);
      btnScale = Math.min(1, btnScale + dt * 8);
      refreshCtaColors();
      draw();
    }

    /** Extra pixels around the bitmap so shadowBlur doesn’t clip in a visible square at the canvas edge */
    const CANVAS_EDGE_PAD = 28;

    function resize() {
      const w = wrap.clientWidth || 280;
      const s = Math.min(Math.max(180, w * 0.98), 320);
      const inner = Math.round(s);
      const dim = inner + 2 * CANVAS_EDGE_PAD;
      canvas.width = dim;
      canvas.height = dim;
      CX = dim / 2;
      CY = dim / 2;
      R = inner / 2;
      draw();
    }

    function handleTap() {
      if (disabledRef.current) return;
      if (phase !== PHASE.IDLE) return;
      btnScale = 0.88;
      hideScale = 1;
      hideSpin = 0;
      hideAlpha = 1;
      phase = PHASE.HIDING;
      phaseT = 0;
    }

    const ro = new ResizeObserver(() => resize());
    ro.observe(wrap);
    resize();
    rafId = requestAnimationFrame(loop);

    const onClick = () => handleTap();
    const onTouchEnd = (e) => {
      e.preventDefault();
      handleTap();
    };
    canvas.addEventListener('click', onClick);
    canvas.addEventListener('touchend', onTouchEnd, { passive: false });

    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
      canvas.removeEventListener('click', onClick);
      canvas.removeEventListener('touchend', onTouchEnd);
    };
  }, []);

  return (
    <div
      ref={wrapRef}
      className={`salon-start-canvas-wrap${disabled ? ' salon-start-canvas-wrap--disabled' : ''}${className ? ` ${className}` : ''}`}
    >
      <canvas
        ref={canvasRef}
        aria-label="Start skin analysis"
        role="img"
      />
    </div>
  );
}
